import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { Eyebrow, Diamond } from "@/components/BrandGeometry";
import { scrollToId } from "@/lib/nav";
import { COMPANY } from "@/lib/content";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden border-b border-line bg-white"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-12">
        {/* Left — editorial column */}
        <div className="px-6 pb-16 pt-12 sm:px-10 sm:pb-20 sm:pt-20 lg:col-span-7 lg:px-16 lg:py-28">
          <Reveal>
            <Eyebrow>Fundraising &amp; Promotions · {COMPANY.area}</Eyebrow>
          </Reveal>

          <h1 className="mt-8 font-display text-[3.25rem] font-medium leading-[1.0] tracking-tight text-navy sm:text-6xl md:text-7xl">
            <MaskedLines
              ariaLabel="Ethical fundraising. Measurable impact."
              lines={[
                "Ethical",
                "fundraising.",
                <span key="g" className="text-brand-green">Measurable impact.</span>,
              ]}
            />
          </h1>

          <Reveal delay={0.55} className="mt-8 max-w-xl">
            <p className="text-base leading-relaxed text-slate-body sm:text-lg">
              Amanah Promotions helps charities acquire committed regular donors through
              professional face-to-face fundraising, respectful public engagement and clear
              campaign oversight.
            </p>
          </Reveal>

          <Reveal delay={0.65} className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Link
              to="/book-a-consultation"
              className="inline-flex items-center justify-center gap-2 bg-brand-green px-7 py-4 text-base font-semibold text-white transition-colors hover:bg-brand-greenDeep"
              data-testid="hero-book-consultation"
            >
              Book a Consultation
              <ArrowRight className="h-4 w-4" />
            </Link>
            <button
              onClick={() => scrollToId("how-we-work")}
              className="inline-flex items-center justify-center gap-2 border border-navy px-7 py-4 text-base font-semibold text-navy transition-colors hover:bg-navy hover:text-white"
              data-testid="hero-explore-how-we-work"
            >
              Explore How We Work
            </button>
          </Reveal>

          <Reveal
            delay={0.75}
            className="mt-14 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-muted"
          >
            <span>Regular-giving campaigns</span>
            <Diamond />
            <span>Donor acquisition</span>
            <Diamond />
            <span>Campaign oversight</span>
          </Reveal>
        </div>

        {/* Right — brand panel showing the full Amanah Promotions logo */}
        <div className="relative flex min-h-[340px] flex-col overflow-hidden bg-navy lg:col-span-5">
          <div className="absolute inset-0 grid-texture opacity-60" aria-hidden="true" />
          <div className="relative flex flex-1 items-center justify-center p-8 sm:p-12">
            <div className="w-full max-w-md bg-white p-8 sm:p-10">
              <img
                src="/amanah-main-logo.png"
                alt="Amanah Promotions — Ethical Fundraising. Measurable Impact."
                className="mx-auto h-auto w-full object-contain"
                style={{ maxHeight: "340px" }}
                data-testid="hero-logo"
              />
            </div>
          </div>
          <div className="relative flex items-center justify-between px-6 pb-6 text-[0.7rem] uppercase tracking-[0.22em] text-white/55">
            <span>Trust</span>
            <span>Responsibility</span>
            <span>Stewardship</span>
          </div>
        </div>
      </div>
    </section>
  );
}
